import { useState, useCallback } from 'react';

export type ColumnType = 'date' | 'phone' | 'email' | 'number' | 'sex' | 'boolean' | 'text';

export interface AnalysisResult {
    delimiter: ',' | ';' | '\t';
    headerRowIndex: number;
    headers: string[];
    columnTypes: ColumnType[];
    sampleRows: string[][];
    allRows: string[][];
    // 1-based record number for each entry in allRows (skips blank lines)
    rowLineNumbers: number[];
}


// Matches phone-number-like strings: 8+ digits with optional +/00 prefix and separators
const PHONE_REGEX = /(?<!\d)(?:\+|00)?[\d\s\-()]{8,}\d(?!\d)/;
const SEX_VALUES = new Set(['m', 'f', 'male', 'female', 'masculino', 'feminino']);
const BOOL_VALUES = new Set(['yes', 'no', 'true', 'false', 'sim', 'não', 'nao', '1', '0']);
const BOM = '﻿';
const REPLACEMENT_CHAR = '�';

function isDateLike(value: string): boolean {
    const v = value.trim();
    if (!v || v.startsWith('0000') || v === '1899-12-30') return false;
    // Allow combined date+time values (e.g. "15/03/2024 10:30", "2024-03-15 10:30:00")
    return /^\d{4}-\d{2}-\d{2}/.test(v) || /^\d{1,2}\/\d{1,2}\/\d{4}/.test(v);
}

function inferColumnType(values: string[]): ColumnType {
    const nonEmpty = values.filter(v => v.trim().length > 0);
    if (nonEmpty.length === 0) return 'text';
    const threshold = nonEmpty.length * 0.5;

    if (nonEmpty.filter(v => isDateLike(v)).length > threshold) return 'date';
    if (nonEmpty.filter(v => PHONE_REGEX.test(v)).length > threshold) return 'phone';
    if (nonEmpty.filter(v => v.includes('@') && v.includes('.')).length > threshold) return 'email';
    if (nonEmpty.filter(v => SEX_VALUES.has(v.trim().toLowerCase())).length > threshold) return 'sex';
    if (nonEmpty.filter(v => BOOL_VALUES.has(v.trim().toLowerCase())).length > threshold) return 'boolean';
    if (nonEmpty.filter(v => v.trim() !== '' && !isNaN(Number(v.trim()))).length > threshold) return 'number';
    return 'text';
}

async function readFileAsText(file: File): Promise<string> {
    const utf8 = await file.text();
    if (utf8.includes(REPLACEMENT_CHAR)) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = e => resolve((e.target?.result as string) ?? '');
            reader.onerror = reject;
            reader.readAsText(file, 'windows-1252');
        });
    }
    return utf8.startsWith(BOM) ? utf8.slice(1) : utf8;
}

function isExcelFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.ods');
}

async function readSheetRowsFromText(text: string): Promise<string[][]> {
    const XLSX = await import('xlsx');
    const workbook = XLSX.read(text, { type: 'string', cellDates: false, raw: false });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
        defval: '',
    }) as string[][];
}

async function readSheetRowsFromFile(file: File): Promise<string[][]> {
    const XLSX = await import('xlsx');
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: false, raw: false });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    return XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
        defval: '',
    }) as string[][];
}

// Shared analysis logic that works on a pre-split 2D array (from Excel or CSV).
function analyseRows(rawRows: string[][], delimiter: ',' | ';' | '\t'): AnalysisResult {
    // Detect header: first row that has the most non-blank columns.
    let headerRowIndex = 0;
    let bestFilled = -1;
    for (let i = 0; i < Math.min(rawRows.length, 50); i++) {
        const filled = rawRows[i].filter(c => c.trim()).length;
        if (filled > bestFilled) { bestFilled = filled; headerRowIndex = i; }
    }

    const rawHeaders = rawRows[headerRowIndex] ?? [];
    const keptIndices = rawHeaders
        .map((h, i) => ({ h, i }))
        .filter(({ h }) => h.trim().length > 0)
        .map(({ i }) => i);
    const headers = keptIndices.map(i => rawHeaders[i]);

    const dataEntries: { cells: string[]; lineNumber: number }[] = [];
    rawRows.slice(headerRowIndex + 1).forEach((row, offset) => {
        if (row.every(c => !c.trim())) return;
        dataEntries.push({
            cells: keptIndices.map(i => (row[i] ?? '').trim()),
            lineNumber: headerRowIndex + 2 + offset,
        });
    });

    const allRows = dataEntries.map(e => e.cells);
    const rowLineNumbers = dataEntries.map(e => e.lineNumber);
    const sampleRows = allRows.slice(0, 20);
    const columnTypes: ColumnType[] = headers.map((_, i) =>
        inferColumnType(sampleRows.map(row => row[i] ?? ''))
    );
    return { delimiter, headerRowIndex, headers, columnTypes, sampleRows, allRows, rowLineNumbers };
}

export async function runCsvAnalysis(file: File): Promise<AnalysisResult> {
    const rows = await readSheetRowsFromText(await readFileAsText(file));
    return analyseRows(rows, ',');
}

async function runExcelAnalysis(file: File): Promise<AnalysisResult> {
    const rows = await readSheetRowsFromFile(file);
    return analyseRows(rows, ',');
}

export function useAnalyseCSV() {
    const [isAnalysing, setIsAnalysing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = useCallback(async (file: File): Promise<AnalysisResult | null> => {
        setIsAnalysing(true);
        setError(null);
        try {
            await new Promise(r => setTimeout(r, 0));
            const result = isExcelFile(file)
                ? await runExcelAnalysis(file)
                : await runCsvAnalysis(file);
            return result;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
            return null;
        } finally {
            setIsAnalysing(false);
        }
    }, []);

    return { run, isAnalysing, error };
}
